import React, { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, Phone } from "lucide-react";
import { PhoneNumber } from "@/models/PhoneNumber";
import {
  getPhoneNumbers,
  deletePhoneNumber,
} from "@/services/phoneNumberService";
import { useToast } from "@/components/ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import PhoneNumberForm from "./PhoneNumberForm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useOrganization } from "../../App";
import { useAuth } from "../../../supabase/auth";
import { supabase } from "../../../supabase/supabase";
import { NoData } from "@/components/ui/no-data";

const PhoneNumbersList: React.FC = () => {
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] =
    useState<PhoneNumber | null>(null);
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();

  const fetchPhoneNumbers = async () => {
    if (!currentOrganization) return;

    setLoading(true);
    try {
      const data = await getPhoneNumbers(currentOrganization.id);
      setPhoneNumbers(data);
      setError(null);
    } catch (err) {
      Sentry.logger.error('Error fetching phone numbers', { error: err, 
        log_source: 'phone_numbers_list',
        organization_id: currentOrganization?.id
      });
      setError("Failed to load phone numbers. Please try again.");
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load phone numbers. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentOrganization) {
      fetchPhoneNumbers();
    }
  }, [currentOrganization]);

  useEffect(() => {
    if (!currentOrganization) return;

    // Set up realtime subscription with organization filter
    const subscription = supabase
      .channel(`phone-numbers-changes-${currentOrganization.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "phone_numbers",
          filter: `organization_id=eq.${currentOrganization.id}`,
        },
        () => fetchPhoneNumbers(),
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [currentOrganization]);

  const handleDelete = async (id: string) => {
    try {
      await deletePhoneNumber(id);
      setPhoneNumbers(phoneNumbers.filter((pn) => pn.id !== id));
      toast({
        title: "Success",
        description: "Phone number deleted successfully",
      });
    } catch (err) {
      Sentry.logger.error('Error deleting phone number', { error: err, 
        log_source: 'phone_numbers_list',
        phone_number_id: id
      });
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete phone number. Please try again.",
      });
    }
  };

  const handleEdit = (phoneNumber: PhoneNumber) => {
    setSelectedPhoneNumber(phoneNumber);
    setIsEditDialogOpen(true);
  };

  const handleFormSuccess = () => {
    fetchPhoneNumbers();
    setIsAddDialogOpen(false);
    setIsEditDialogOpen(false);
    setSelectedPhoneNumber(null);
  };

  return (
    <div className="container mx-auto py-6 bg-background text-foreground">
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : error ? (
          <div className="bg-destructive/10 p-4 rounded-md text-destructive">
            {error}
          </div>
        ) : phoneNumbers.length === 0 ? (
          <NoData
            icon={<Phone className="h-12 w-12 text-muted-foreground/50" />}
            title="No phone numbers yet"
            description="Add your first phone number to start managing your inbound and outbound calls."
            actionButton={
              <Button
                onClick={() => setIsAddDialogOpen(true)}
                variant="primaryOutline"
                className="mt-2"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Phone Number
              </Button>
            }
          />
        ) : (
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Phone Number</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>SIP Domain</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {phoneNumbers.map((phoneNumber) => (
                  <TableRow key={phoneNumber.id}>
                    <TableCell className="font-medium">
                      {phoneNumber.phone_number}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`capitalize px-2 py-1 rounded-full text-xs ${phoneNumber.type === "inbound" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"}`}
                      >
                        {phoneNumber.type}
                      </span>
                    </TableCell>
                    <TableCell>
                      {phoneNumber.type === "outbound"
                        ? phoneNumber.termination_sip_domain ||
                          phoneNumber.endpoint_url ||
                          "-"
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(phoneNumber)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              Delete Phone Number
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this phone number?
                              This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(phoneNumber.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Add Phone Number Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Phone Number</DialogTitle>
          </DialogHeader>
          <PhoneNumberForm onSuccess={handleFormSuccess} />
        </DialogContent>
      </Dialog>

      {/* Edit Phone Number Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Phone Number</DialogTitle>
          </DialogHeader>
          {selectedPhoneNumber && (
            <PhoneNumberForm
              phoneNumber={selectedPhoneNumber}
              onSuccess={handleFormSuccess}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PhoneNumbersList;
